// Registers the global helpers for the cart-line editing popup.
// New architecture (2024-06):
//   • Popup markup lives in snippets/edit-cart-line-popup.liquid
//   • Styling lives in assets/component-edit-cart-line.css
//   • This script only handles logic + data binding.

(function () {
  let overlayEl = null;

  /* ---------------------------------------------------------------------------
   * 1.  Markup handling (preload / show / hide)
   * ------------------------------------------------------------------------- */
  function preloadEditCartLine() {
    if (overlayEl) return overlayEl;

    const tpl = document.getElementById('edit-cart-line-template');
    if (!tpl || !tpl.content) {
      console.warn('[editCartLine] template "edit-cart-line-template" not found');
      return null;
    }

    // Clone once and store
    overlayEl = tpl.content.firstElementChild.cloneNode(true);
    document.body.appendChild(overlayEl);

    // Close interactions
    overlayEl.addEventListener('click', (e) => {
      if (e.target === overlayEl) hideOverlay();
    });
    const closeBtn = overlayEl.querySelector('.edit-cart-line-close');
    if (closeBtn) closeBtn.addEventListener('click', hideOverlay);

    // Base accordion behaviour
    attachAccordionLogic(overlayEl);

    return overlayEl;
  }

  function showOverlay() {
    if (!overlayEl) preloadEditCartLine();
    if (!overlayEl) return;
    overlayEl.style.display = 'flex';
    document.body.classList.add('overflow-hidden');
  }
  function hideOverlay() {
    if (!overlayEl) return;
    overlayEl.style.display = 'none';
    document.body.classList.remove('overflow-hidden');
  }

  /* ---------------------------------------------------------------------------
   * 2.  Accordion logic (colour step → size step)
   * ------------------------------------------------------------------------- */
  function attachAccordionLogic(root) {
    const step1 = root.querySelector('#step1');
    const step2 = root.querySelector('#step2');
    if (!step1 || !step2) return;

    const header1 = step1.querySelector('.accordion-header');
    const confirmColorBtn = step1.querySelector('.confirm-button');

    header1?.addEventListener('click', () => {
      step1.classList.toggle('active');
    });
    confirmColorBtn?.addEventListener('click', () => {
      step1.classList.remove('active');
      step2.classList.add('active');
    });
  }

  /* ---------------------------------------------------------------------------
   * 3.  Cart mutation helper
   * ------------------------------------------------------------------------- */
  async function replaceVariantInCart(originalLineItemId, newVariantId) {
    if (!window.CartUtils) {
      console.error('[editCartLine] CartUtils not found');
      return;
    }
    window.isEditingCartLine = true;
    try {
      /* -------------------------------------------------------------------
       * 0.  Fetch current cart to get variant IDs & quantities
       * ----------------------------------------------------------------- */
      const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!cartRes.ok) throw new Error('Unable to fetch cart');
      const cartData = await cartRes.json();

      const originalLine = cartData.items?.find((i) => i.key === originalLineItemId);
      if (!originalLine) {
        console.warn('[editCartLine] Original line not found – fallback to add only');
        /* Fallback: just add new variant */
        const addBody = new FormData();
        addBody.append('id', newVariantId);
        addBody.append('quantity', 1);
        await CartUtils.mutateCartAndRefresh({
          url: routes.cart_add_url,
          body: addBody,
          source: 'edit-cart-line-add-fallback',
        });
        return;
      }

      /* -------------------------------------------------------------------
       * 1.  Quietly remove/decrease old line WITHOUT refreshing UI yet
       * ----------------------------------------------------------------- */
      const remainingQty = Math.max(0, originalLine.quantity - 1);
      const changeBody = new URLSearchParams();
      changeBody.append('id', originalLine.key);
      changeBody.append('quantity', remainingQty);

      const changeRes = await fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: changeBody.toString()
      });

      if (!changeRes.ok) throw new Error('Failed to update original line item');

      /* -------------------------------------------------------------------
       * 2.  Add new variant and copy properties, THEN refresh UI
       * ----------------------------------------------------------------- */
      const addBody = new FormData();
      addBody.append('items[0][id]', newVariantId);
      addBody.append('items[0][quantity]', 1);

      if (originalLine.properties && Object.keys(originalLine.properties).length > 0) {
        Object.entries(originalLine.properties).forEach(([k, v]) => {
          if (v != null) {
            addBody.append(`items[0][properties][${k}]`, v);
          }
        });
      }

      // Mark this item as manually edited by the user, so bots don't overwrite user choices
      addBody.append(`items[0][properties][_user_selected]`, '1');

      await CartUtils.mutateCartAndRefresh({
        url: routes.cart_add_url,
        body: addBody,
        source: 'edit-cart-line-replace',
      });
    } catch (err) {
      console.error('[editCartLine] Fehler beim Aktualisieren des Warenkorbs:', err);
      alert('Fehler beim Aktualisieren des Warenkorbs. Bitte versuchen Sie es erneut.');
    } finally {
      // Delay clearing the flag so any straggling PerformanceObserver
      // entries from the /cart/add fetch don't trigger the bot prematurely
      setTimeout(() => { window.isEditingCartLine = false; }, 600);
    }
  }

  /* ---------------------------------------------------------------------------
   * 4.  Public API – editCartLine()
   * ------------------------------------------------------------------------- */
  window.preloadEditCartLine = preloadEditCartLine;

  window.editCartLine = async function editCartLine(lineItemId, colorCollection, sizeCollection, mappingCollection) {
    preloadEditCartLine();
    if (!overlayEl) return;

    /* We wait with showing the popup until after we have finished all initial
       rendering and the current variant pre-selection. This prevents a visual
       flash where no option is selected for a split second. */

    const popup = overlayEl.querySelector('.edit-cart-line-popup');

    // Reset previous state & selections
    popup.querySelectorAll('.color-grid, .size-grid').forEach(g => (g.innerHTML = ''));
    overlayEl.querySelectorAll('.color-card.selected, .size-card.selected').forEach(el => el.classList.remove('selected'));
    const step1 = overlayEl.querySelector('#step1');
    const step2 = overlayEl.querySelector('#step2');
    if (step1) step1.classList.add('active');
    if (step2) step2.classList.remove('active');

    const hasColors = Array.isArray(colorCollection) && colorCollection.length;
    const hasSizes = Array.isArray(sizeCollection) && sizeCollection.length;

    /* ---- dynamic title & accordion headers ------------------------------ */
    const titleEl = popup.querySelector('.edit-cart-line-popup-header');
    if (titleEl) {
      if (hasColors && hasSizes) titleEl.textContent = 'Produkt Anpassen';
      else if (hasColors) titleEl.textContent = 'Farbe Anpassen';
      else if (hasSizes) titleEl.textContent = 'Grösse Anpassen';
    }

    // Hide accordion section titles when only one step is present
    const onlyOneStep = (hasColors && !hasSizes) || (!hasColors && hasSizes);
    popup.querySelectorAll('.accordion-header').forEach(h => {
      h.style.display = onlyOneStep ? 'none' : '';
    });

    // Add a helper class to allow CSS tweaks when only one step is present
    if (onlyOneStep) popup.classList.add('single-step');
    else popup.classList.remove('single-step');

    /* ---- helpers to render option cards ---------------------------------- */
    const createColorCard = (color) => {
      const card = document.createElement('div');
      card.className = 'color-card';
      card.dataset.key = typeof color === 'string' ? color : (color.key || color.name || '');
      const name = typeof color === 'string' ? color : (color.name || color.label || '');
      const image = (typeof color === 'object' && (color.imageURL || color.image)) ? (color.imageURL || color.image) : '';
      card.innerHTML = `${image ? `<img src="${image}" alt="${name}">` : ''}<span class="color-label">${name}</span>`;
      return card;
    };

    const createSizeCard = (size) => {
      const card = document.createElement('div');
      card.className = 'size-card';
      card.dataset.key = size.key;
      card.innerHTML = `<span class="size-name">${size.name}</span><span class="size-dimensions">${size.dimensions || ''}</span>`;
      return card;
    };

    /* ---- variant resolver ----------------------------------------------- */
    const resolveVariantId = (colorKey, sizeKey) => {
      if (!Array.isArray(mappingCollection)) return null;
      const match = mappingCollection.find(m => {
        const colorMatches = colorKey == null || m.colorKey == null || m.colorKey === colorKey;
        const sizeMatches = sizeKey == null || m.sizeKey == null || m.sizeKey === sizeKey;
        return colorMatches && sizeMatches;
      });
      if (!match) return null;
      return match.variantId || match.variationId || match.id || match.variant_id;
    };

    /* ---- reference elements --------------------------------------------- */
    const colorGrid = popup.querySelector('.color-grid');
    const sizeGrid = popup.querySelector('.size-grid');
    const colorBtn = popup.querySelector('.color-confirm-button');
    const sizeBtn = popup.querySelector('.size-confirm-button');

    // Track the variant currently in the cart so we can detect no-op edits
    let originalVariantId = null;

    /* ---- conditional setup ---------------------------------------------- */
    //  Colours --------------------------------------------------------------
    if (hasColors) {
      step1.style.display = '';
      colorGrid.innerHTML = '';
      colorCollection.forEach(color => {
        const card = createColorCard(color);
        card.addEventListener('click', () => {
          colorGrid.querySelectorAll('.color-card.selected').forEach(c => c.classList.remove('selected'));
          card.classList.add('selected');
        });
        colorGrid.appendChild(card);
      });
    } else {
      step1.style.display = 'none';
      step2.classList.add('active');
    }

    //  Sizes ---------------------------------------------------------------
    if (hasSizes) {
      step2.style.display = '';
      sizeGrid.innerHTML = '';
      sizeCollection.forEach(size => {
        const card = createSizeCard(size);
        card.addEventListener('click', () => {
          sizeGrid.querySelectorAll('.size-card.selected').forEach(c => c.classList.remove('selected'));
          card.classList.add('selected');
        });
        sizeGrid.appendChild(card);
      });
    } else {
      step2.style.display = 'none';
    }

    /* ---- pre-select current variant ------------------------------------- */
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('Failed to fetch cart');
      const cartData = await res.json();

      const currentLine = cartData.items?.find(i => i.key === lineItemId);
      if (currentLine) {
        const currentVariantId = currentLine.variant_id || currentLine.id;
        originalVariantId = String(currentVariantId);

        const mappingEntry = Array.isArray(mappingCollection)
          ? mappingCollection.find(m => (
            m.variantId == currentVariantId ||
            m.variationId == currentVariantId ||
            m.id == currentVariantId ||
            m.variant_id == currentVariantId
          ))
          : null;

        if (mappingEntry) {
          const { colorKey, sizeKey } = mappingEntry;

          if (hasColors && colorKey != null) {
            colorGrid.querySelectorAll('.color-card').forEach(card => {
              if (card.dataset.key == colorKey) card.classList.add('selected');
            });
          }

          if (hasSizes && sizeKey != null) {
            sizeGrid.querySelectorAll('.size-card').forEach(card => {
              if (card.dataset.key == sizeKey) card.classList.add('selected');
            });
          }
        }
      }
    } catch (err) {
      console.warn('[editCartLine] Unable to preselect variant:', err);
    }

    /* All initial work is done – now reveal the overlay to the user */
    showOverlay();

    /* ---- buttons -------------------------------------------------------- */
    if (colorBtn) {
      colorBtn.onclick = () => {
        if (!hasColors) return;
        const selectedColor = colorGrid.querySelector('.color-card.selected');
        if (!selectedColor) {
          alert('Bitte wählen Sie eine Farbe aus.');
          return;
        }
        if (hasSizes) {
          // Accordion will already move to step2
          return;
        }
        const variantId = resolveVariantId(selectedColor.dataset.key, null);
        if (!variantId) {
          alert('Ausgewählte Variante nicht verfügbar.');
          return;
        }
        // Skip mutation if variant has not changed
        if (String(variantId) === originalVariantId) {
          hideOverlay();
          return;
        }
        hideOverlay();
        replaceVariantInCart(lineItemId, variantId);
      };
    }

    if (sizeBtn) {
      sizeBtn.onclick = () => {
        if (!hasSizes) return;
        const selectedSize = sizeGrid.querySelector('.size-card.selected');
        if (!selectedSize) {
          alert('Bitte wählen Sie eine Grösse aus.');
          return;
        }
        const selectedColor = hasColors ? colorGrid.querySelector('.color-card.selected') : null;
        if (hasColors && !selectedColor) {
          alert('Bitte wählen Sie eine Farbe aus.');
          return;
        }
        const variantId = resolveVariantId(selectedColor ? selectedColor.dataset.key : null, selectedSize.dataset.key);
        if (!variantId) {
          alert('Ausgewählte Variante nicht verfügbar.');
          return;
        }
        // Skip mutation if variant has not changed
        if (String(variantId) === originalVariantId) {
          hideOverlay();
          return;
        }
        hideOverlay();
        replaceVariantInCart(lineItemId, variantId);
      };
    }

    // Debug ----------------------------------------------------------------
    console.info('[editCartLine] lineItemId:', lineItemId);
    console.info('[editCartLine] colors:', colorCollection);
    console.info('[editCartLine] sizes:', sizeCollection);
    console.info('[editCartLine] mapping:', mappingCollection);
  };

  /* ---------------------------------------------------------------------------
   * 5.  Auto-preload as soon as edit buttons exist
   * ------------------------------------------------------------------------- */
  function autoPreload() {
    if (document.querySelector('.edit-cart-line-btn')) {
      preloadEditCartLine();
      return;
    }
    const mo = new MutationObserver((_, obs) => {
      if (document.querySelector('.edit-cart-line-btn')) {
        preloadEditCartLine();
        obs.disconnect();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoPreload);
  } else {
    autoPreload();
  }
})();
